import { z } from "@hono/zod-openapi";
import { drivingV1 } from "../../dutyStatus/driving";
import { logCertifiedV1 } from "../../compliance/logCertified";
import { loginV1 } from "../../session/login";
import { logoutV1 } from "../../session/logout";
import { offDutyV1 } from "../../dutyStatus/offDuty";
import { onDutyV1 } from "../../dutyStatus/onDuty";
import { personalConveyanceV1 } from "../../specialDriving/personalConveyance";
import { sleeperBerthV1 } from "../../dutyStatus/sleeperBerth";
import { specialDrivingEndedV1 } from "../../specialDriving/specialDrivingEnded";
import { yardMoveV1 } from "../../specialDriving/yardMove";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * A change in a driver's ELD status, straight off the hours-of-service event feed.
 *
 * High volume, and context rather than alarm: most of these kinds carry nothing but the time, and the unit an
 * event names is a reference rather than a fact.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const dutyStatusChangeKinds = [
  "logCertified",
  "driving",
  "offDuty",
  "onDuty",
  "sleeperBerth",
  "login",
  "logout",
  "personalConveyance",
  "specialDrivingEnded",
  "yardMove",
] as const satisfies readonly TmsEventKind[];

export type DutyStatusChangeKind = (typeof dutyStatusChangeKinds)[number];

/**
 * The type of `tms_event.duty_status_change.facts`: any of this subject's kinds, at any version it declares.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const DutyStatusChangeFactsSchema = z.discriminatedUnion("kind", [
  logCertifiedV1.facts,
  drivingV1.facts,
  offDutyV1.facts,
  onDutyV1.facts,
  sleeperBerthV1.facts,
  loginV1.facts,
  logoutV1.facts,
  personalConveyanceV1.facts,
  specialDrivingEndedV1.facts,
  yardMoveV1.facts,
]);

export type DutyStatusChangeFacts = z.infer<typeof DutyStatusChangeFactsSchema>;

/**
 * What a DutyStatusChange normalizer produces: references and facts, without the base the writer supplies.
 */
export type DutyStatusChangeBody = TmsEventBodyOf<DutyStatusChangeKind>;
